import { StringContext } from "../../../core/StringContext";
import { StringDevModule } from "../core/StringDevModule";
import { RULERS_CSS } from "./StringRulers.css";
import { StringData } from "../../../core/StringData";
import { RulersManager } from "./RulersManager";
import { RulersOverlay, RulersRowLayout } from "./RulersOverlay";
import type { RulersLayoutGrid } from "./models/RulersLayoutGrid";
import { StringRulersTrigger, RulersTriggerAction } from "./models/StringRulersTrigger";
import { RULER_MODES, MODE_ORDER, type RulerModeId } from "./models/RulerMode";
import { overlaySlotRegistry } from "../core/StringDevtoolsOverlayLayout";
import { StringDevViewportLayer } from "../core/StringDevViewportLayer";
import { StringDevPersistedState } from "../core/StringDevPersistedState";
import { StringDevtoolSubBadge } from "../../../models/devtools/StringDevtool";

const GLOBAL_ELEMENT_ID = "__string-rulers-global__";

const DEFAULT_TRIGGERS: StringRulersTrigger[] = [];

/**
 * StringDevRulers — developer utility module for guide-line rulers.
 *
 * Usage:
 *   st.use(StringDevRulers)
 *   st.use(StringDevRulers, { triggers: [...], grid: { type: "columns", count: 12, ... } })
 *
 * Default trigger: Shift + R
 *
 * Settings:
 *   triggers               — array of trigger descriptors (keyboard / element / event)
 *   rulers-snap            — grid step in px (0 = off)
 *   rulers-snap-elements   — snap to [string] element edges (default: true)
 *   rulers-snap-threshold  — snap pull radius in px (default: 8)
 *   rulers-snap-selector   — CSS selector for snap targets
 *   grid                   — RulersLayoutGrid | RulersLayoutGrid[]
 *
 * Snap visual feedback:
 *   amber → snapping to a DOM element edge / center
 *   green → snapping to a layout-grid line
 */
export class StringDevRulers extends StringDevModule {
  private manager: RulersManager = new RulersManager();
  private overlay: RulersOverlay | null = null;
  private visible: boolean = false;
  private dockDisabled: boolean = false;
  private currentModeId: RulerModeId = "default";

  private readonly modeStore = new StringDevPersistedState<RulerModeId>({
    moduleId: "rulers",
    name: "mode",
    default: "default" as RulerModeId,
    validate: (raw) =>
      typeof raw === "string" && raw in RULER_MODES ? (raw as RulerModeId) : null,
    legacyKeys: ["string-rulers:mode"],
  });
  private readonly viewportLayer: StringDevViewportLayer;

  // ── Trigger infrastructure ────────────────────────────

  private _kbHandlers: Array<(e: KeyboardEvent) => void> = [];
  private _elHandlers: Array<{ el: Element; event: string; fn: EventListener }> = [];
  private _evHandlers: Array<{ name: string; fn: (...args: unknown[]) => void }> = [];

  static override devtool = {
    id: "rulers",
    label: "Rulers",
    icon: "ruler",
    order: 10,
    group: 1,
    hotkey: { key: "R", shiftKey: true },
    styles: RULERS_CSS,
    connects: { global: false, keys: ["rulers"] },
  };

  constructor(context: StringContext) {
    super(context);
    this.attributesToMap = [];
    this.viewportLayer = this.acquireViewportLayer("devtools-rulers", 10020);
  }

  // ── Lifecycle ─────────────────────────────────────────

  onInit(): void {
    this.applyStyleSettings();
    this.mountOverlay();
  }

  onSettingsChange(): void {
    this.applyStyleSettings();
  }

  onSubscribe(): void {
    const raw = (this.settings as Record<string, unknown>)["triggers"];
    const triggers: StringRulersTrigger[] = Array.isArray(raw)
      ? (raw as StringRulersTrigger[])
      : DEFAULT_TRIGGERS;
    this._bindTriggers(triggers);
  }

  onUnsubscribe(): void {
    this._unbindTriggers();
  }

  onFrame(data: StringData): void {
    if (!this.visible || !this.overlay) return;
    this.pushScrollToOverlay(data);
  }

  onResize(): void {
    if (!this.overlay) return;
    this.syncOverlayMetrics();
    const lines = this.manager.getLines(GLOBAL_ELEMENT_ID, this.currentModeId);
    this.overlay.recalculateBindings(lines);
    this.manager.save(GLOBAL_ELEMENT_ID, this.currentModeId);
    this.overlay.refresh(lines);
  }

  destroy(): void {
    this._unbindTriggers();
    this.overlay?.destroy();
    this.overlay = null;
    this.manager.clearAll();
    this.clearStyleSettings();
    super.destroy();
  }

  // ── Public API ────────────────────────────────────────

  toggle(): void {
    this.setVisible(!this.visible);
  }
  show(): void {
    this.setVisible(true);
  }
  hide(): void {
    this.setVisible(false);
  }
  clear(): void {
    this.manager.removeAllLines(GLOBAL_ELEMENT_ID, this.currentModeId);
    this.overlay?.refresh([]);
  }

  public isVisible(): boolean {
    return this.visible;
  }

  protected getDevtoolActiveState(): boolean {
    return this.visible;
  }

  protected setDevtoolActiveState(active: boolean): void {
    this.setDockActive(active);
  }

  protected override getDevtoolSubBadges(): StringDevtoolSubBadge[] {
    return [
      {
        id: "clear-rulers",
        icon: "noplus",
        label: "Clear guides",
        selectorAttribute: "data-string-rulers-clear-all",
        onClick: () => this.clear(),
      },
    ];
  }

  // ── Trigger binding ───────────────────────────────────

  private _bindTriggers(triggers: StringRulersTrigger[]): void {
    for (const t of triggers) {
      if (t.type === "keyboard") {
        const handler = (e: KeyboardEvent) => {
          if (
            e.key === t.key &&
            e.shiftKey === (t.shiftKey ?? false) &&
            e.ctrlKey === (t.ctrlKey ?? false) &&
            e.altKey === (t.altKey ?? false) &&
            e.metaKey === (t.metaKey ?? false)
          ) {
            e.preventDefault();
            this._applyAction(t.action);
          }
        };
        window.addEventListener("keydown", handler);
        this._kbHandlers.push(handler);
      } else if (t.type === "element") {
        const eventName = t.event ?? "click";
        for (const el of Array.from(document.querySelectorAll(t.selector))) {
          const fn: EventListener = () => this._applyAction(t.action);
          el.addEventListener(eventName, fn);
          this._elHandlers.push({ el, event: eventName, fn });
        }
      } else if (t.type === "event") {
        const fn = () => this._applyAction(t.action);
        this.events.on(t.name, fn);
        this._evHandlers.push({ name: t.name, fn });
      }
    }
  }

  private _unbindTriggers(): void {
    for (const h of this._kbHandlers) window.removeEventListener("keydown", h);
    this._kbHandlers = [];
    for (const { el, event, fn } of this._elHandlers) el.removeEventListener(event, fn);
    this._elHandlers = [];
    for (const { name, fn } of this._evHandlers) this.events.off(name, fn);
    this._evHandlers = [];
  }

  private _applyAction(action: RulersTriggerAction = "toggle"): void {
    if (this.dockDisabled) {
      return;
    }
    if (action === "show") this.show();
    else if (action === "hide") this.hide();
    else this.toggle();
  }

  // ── Visibility ────────────────────────────────────────

  private setVisible(value: boolean): void {
    if (value && this.dockDisabled) {
      return;
    }
    if (this.visible === value) {
      return;
    }
    this.visible = value;
    this.overlay?.setVisible(value);
    overlaySlotRegistry.emitLayoutChange();
    this.emitDevtoolState();
  }

  private setDockActive(active: boolean): void {
    this.dockDisabled = !active;
    this.setVisible(active);
  }

  // ── Overlay management ────────────────────────────────

  private mountOverlay(): void {
    if (this.overlay) return;

    const elementId = GLOBAL_ELEMENT_ID;

    this.currentModeId = this.modeStore.read();

    this.manager.load(elementId, this.currentModeId);

    const s = this.settings as Record<string, unknown>;
    const snapStep = typeof s["rulers-snap"] === "number" ? (s["rulers-snap"] as number) : 0;
    const snapToElements =
      typeof s["rulers-snap-elements"] === "boolean"
        ? (s["rulers-snap-elements"] as boolean)
        : true;
    const snapThreshold =
      typeof s["rulers-snap-threshold"] === "number" ? (s["rulers-snap-threshold"] as number) : 8;
    const snapSelector =
      typeof s["rulers-snap-selector"] === "string"
        ? (s["rulers-snap-selector"] as string)
        : "[string],[data-string]";
    const layoutGrids = this._resolveGrids(s["grid"]);

    const rulersColumns =
      typeof s["rulers-columns"] === "number" ? (s["rulers-columns"] as number) : 0;
    const rulersMargin =
      typeof s["rulers-margin"] === "string" ? (s["rulers-margin"] as string) : "0px";
    const rulersGap = typeof s["rulers-gap"] === "string" ? (s["rulers-gap"] as string) : "0px";
    const columnLayout =
      rulersColumns > 0
        ? { columns: rulersColumns, margin: rulersMargin, gap: rulersGap }
        : undefined;

    const rulersRowSize = typeof s["rulers-rows"] === "string" ? (s["rulers-rows"] as string) : "";
    const rulersRowGap =
      typeof s["rulers-rows-gap"] === "string" ? (s["rulers-rows-gap"] as string) : "0px";
    const rowLayout: RulersRowLayout | undefined = rulersRowSize
      ? { size: rulersRowSize, gap: rulersRowGap }
      : undefined;

    this.overlay = new RulersOverlay(
      {
        onLineCreated: (axis, position, targetLabel, targetSelector, targetEdge) => {
          this.manager.addLine(
            elementId,
            axis,
            position,
            targetLabel,
            this.currentModeId,
            targetSelector,
            targetEdge,
          );
          this.overlay!.refresh(this.manager.getLines(elementId, this.currentModeId));
        },
        onLineMoved: (lineId, position, targetLabel) => {
          this.manager.moveLine(elementId, lineId, position, targetLabel, this.currentModeId);
          this.overlay!.refresh(this.manager.getLines(elementId, this.currentModeId));
        },
        onLineRemoved: (lineId) => {
          this.manager.removeLine(elementId, lineId, this.currentModeId);
          this.overlay!.refresh(this.manager.getLines(elementId, this.currentModeId));
        },
        onDragCommit: (lineId, position, targetLabel, targetSelector, targetEdge) => {
          this.manager.moveLine(elementId, lineId, position, targetLabel, this.currentModeId);
          const line = this.manager.findLine(elementId, lineId, this.currentModeId);
          if (line) {
            line.targetSelector = targetSelector;
            line.targetEdge = targetEdge;
          }
          this.manager.save(elementId, this.currentModeId);
          this.overlay!.refresh(this.manager.getLines(elementId, this.currentModeId));
        },
        onModeToggle: () => this.cycleMode(),
      },
      {
        snapStep,
        snapToElements,
        snapThreshold,
        snapSelector,
        layoutGrids,
        columnLayout,
        rowLayout,
        scrollTarget: this.data.scroll.elementContainer,
      },
      this.data.scroll.container,
      this.viewportLayer,
    );

    this.syncOverlayMetrics();
    this.overlay.setMode(RULER_MODES[this.currentModeId]);
    this.overlay.setVisible(this.visible);
    this.overlay.refresh(this.manager.getLines(elementId, this.currentModeId));
  }

  // ── Mode switching ──────────────────────────────────────

  private cycleMode(): void {
    const idx = MODE_ORDER.indexOf(this.currentModeId);
    const nextIdx = (idx + 1) % MODE_ORDER.length;
    this.switchMode(MODE_ORDER[nextIdx]);
  }

  private switchMode(modeId: RulerModeId): void {
    const elementId = GLOBAL_ELEMENT_ID;

    // Persist current mode's lines
    this.manager.save(elementId, this.currentModeId);

    // Switch
    this.currentModeId = modeId;
    this.manager.load(elementId, modeId);

    // Update overlay
    this.overlay?.setMode(RULER_MODES[modeId]);
    this.overlay?.refresh(this.manager.getLines(elementId, modeId));

    this.modeStore.write(modeId);
  }

  private applyStyleSettings(): void {
    const s = this.settings as Record<string, unknown>;
    const lineColor =
      typeof s["rulers-color"] === "string"
        ? (s["rulers-color"] as string)
        : "rgba(0, 120, 255, 0.75)";
    const lineOpacityRaw =
      typeof s["rulers-opacity"] === "number" ? (s["rulers-opacity"] as number) : 1;
    const lineOpacity = Math.max(0, Math.min(1, lineOpacityRaw));

    document.documentElement.style.setProperty("--string-rulers-line-color", lineColor);
    document.documentElement.style.setProperty("--string-rulers-line-opacity", String(lineOpacity));
  }

  private clearStyleSettings(): void {
    document.documentElement.style.removeProperty("--string-rulers-line-color");
    document.documentElement.style.removeProperty("--string-rulers-line-opacity");
  }

  /** Normalises the `grid` setting to RulersLayoutGrid[] (or undefined). */
  private _resolveGrids(raw: unknown): RulersLayoutGrid[] | undefined {
    if (!raw) return undefined;
    if (Array.isArray(raw)) return raw as RulersLayoutGrid[];
    if (typeof raw === "object") return [raw as RulersLayoutGrid];
    return undefined;
  }

  private syncOverlayMetrics(): void {
    if (!this.overlay) return;
    this.pushScrollToOverlay(this.data);
  }

  private pushScrollToOverlay(data: StringData): void {
    if (!this.overlay) return;
    const container = data.scroll.elementContainer;
    const horizontal = data.scroll.direction === "horizontal";
    const scrollX = horizontal ? container.scrollLeft : 0;
    const scrollY = horizontal ? 0 : container.scrollTop;
    this.overlay.updateScroll(
      scrollX,
      scrollY,
      data.viewport.windowWidth,
      data.viewport.windowHeight,
      data.viewport.contentWidth,
      data.viewport.contentHeight,
    );
  }
}

export { StringDevRulers as StringRulers };
